import { AlertCircle, LogIn, RefreshCw, Home } from 'lucide-react';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { DiagnosticLogs } from '@/components/common/DiagnosticLogs';
import { logger } from '@/lib/logger';
import { signOutSession } from '@/lib/sign-out';
import { isDynamicImportError, reloadOnceForChunkError } from '@/utils/lazyRetry';
import FallbackSpinner from './FallbackSpinner';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  isAuthError: boolean;
  isReloadingForChunkError?: boolean;
}

const AuthErrorFallback = ({
  error,
  errorInfo,
  isAuthError,
}: {
  error?: Error;
  errorInfo?: ErrorInfo;
  isAuthError: boolean;
}) => {
  const isModuleLoadError = isDynamicImportError(error);

  const handleSignOut = async () => {
    try {
      await signOutSession();
      window.location.href = '/member/login';
    } catch (error) {
      logger.error('Error signing out:', error);
      window.location.href = '/member/login';
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const errorContext = isAuthError
    ? 'Authentication Error'
    : isModuleLoadError
      ? 'Module Load Error'
      : error?.message || 'Application Error';

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[hsl(var(--background))]">
      <div className="max-w-lg w-full bg-[hsl(var(--card))] rounded-2xl shadow-[var(--shadow-xl)] border border-[hsl(var(--border))]">
        {/* Header */}
        <div className="text-center px-8 pt-10 pb-6">
          <div className="mx-auto mb-5 w-16 h-16 bg-[hsl(var(--error)/0.08)] rounded-full flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-[hsl(var(--error))]" />
          </div>
          <h1 className="text-2xl font-semibold text-[hsl(var(--foreground))] tracking-tight">
            {isAuthError ? "Let's get you signed back in" : 'Something went wrong'}
          </h1>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
            {isAuthError
              ? 'Your session may have expired. Signing in again will get you right back.'
              : isModuleLoadError
                ? "A recent update couldn't load properly. A quick refresh should fix it."
                : "We couldn't load this page. Please try one of the options below."}
          </p>
        </div>

        {/* Content */}
        <div className="px-8 pb-8 space-y-5">
          {process.env.NODE_ENV === 'development' && (
            <div className="bg-[hsl(var(--secondary))] p-4 rounded-xl">
              <p className="text-sm font-medium text-[hsl(var(--foreground))] mb-1">
                Error Details:
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] font-mono break-words">
                {error?.message}
              </p>
              {errorInfo && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer text-[hsl(var(--muted-foreground))]">
                    Stack trace
                  </summary>
                  <pre className="text-xs mt-2 overflow-auto max-h-40 text-[hsl(var(--muted-foreground))]">
                    {errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}

          {/* Help tips */}
          <div className="bg-[hsl(var(--secondary))] p-4 rounded-xl space-y-2">
            <p className="text-sm font-medium text-[hsl(var(--foreground))]">
              A few things that might help:
            </p>
            <ul className="text-sm text-[hsl(var(--muted-foreground))] space-y-1 list-disc list-inside">
              <li>Clear your browser cookies and cache</li>
              <li>Try a different browser or incognito mode</li>
              <li>Check your internet connection</li>
              {isModuleLoadError && (
                <li>
                  Hard refresh: <span className="font-mono text-xs">Cmd+Shift+R</span>
                </li>
              )}
            </ul>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2.5">
            {isAuthError ? (
              <>
                <button
                  onClick={handleSignOut}
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In Again
                </button>
                <button
                  onClick={handleReload}
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-full transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Refreshing
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleReload}
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reload Page
                </button>
                <button
                  onClick={handleGoHome}
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-full transition-colors"
                >
                  <Home className="h-4 w-4" />
                  Go to Home
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-full transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </button>
              </>
            )}
          </div>

          {/* Diagnostic logs */}
          <DiagnosticLogs errorContext={errorContext} />
        </div>
      </div>
    </div>
  );
};

export class AuthErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    isAuthError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    const isAuthError =
      error.message?.includes('No access token') ||
      error.message?.includes('Authentication') ||
      error.message?.includes('Unauthorized') ||
      error.message?.includes('session') ||
      error.message?.includes('expired');

    return {
      hasError: true,
      error,
      isAuthError,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Uncaught error:', error, errorInfo);

    // A chunk failing to load usually means a new deploy replaced the hashed
    // asset files. Reload once (sessionStorage-guarded) to pick up the fresh
    // build instead of stranding the user on an error screen.
    if (isDynamicImportError(error) && reloadOnceForChunkError()) {
      this.setState({ errorInfo, isReloadingForChunkError: true });
      return;
    }

    this.setState({ errorInfo });
  }

  public render() {
    if (this.state.isReloadingForChunkError) {
      return <FallbackSpinner />;
    }

    if (this.state.hasError) {
      return (
        <AuthErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          isAuthError={this.state.isAuthError}
        />
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;
